import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { id: "new", label: "New", color: "#3B82F6" },
  { id: "contacted", label: "Contacted", color: "#94A3B8" },
  { id: "appointment", label: "Appointment", color: "#A78BFA" },
  { id: "negotiation", label: "Negotiation", color: "#F59E0B" },
  { id: "approved", label: "Approved", color: "#10B981" },
  { id: "sold", label: "Sold", color: "#D4AF37" },
  { id: "lost", label: "Lost", color: "#E63946" },
];

export default function AdminCRM() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", stage: "new", notes: "" });

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.customer_name) { toast.error("Name required"); return; }
    try {
      await api.post("/leads", form);
      toast.success("Lead added");
      setOpen(false);
      setForm({ customer_name: "", email: "", phone: "", stage: "new", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const moveStage = async (lead, newStage) => {
    try {
      await api.put(`/leads/${lead.id}`, { stage: newStage });
      load();
    } catch (e) { toast.error("Failed to move"); }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead for ${lead.customer_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      setLeads((ls) => ls.filter((x) => x.id !== lead.id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const onDragStart = (e, lead) => e.dataTransfer.setData("text/plain", lead.id);
  const onDrop = (e, stageId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const l = leads.find((x) => x.id === id);
    if (l && l.stage !== stageId) moveStage(l, stageId);
  };

  return (
    <AdminShell title="CRM Pipeline">
      <div className="flex justify-between mb-6">
        <div className="label-eyebrow">{leads.length} leads</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta rounded-sm" data-testid="add-lead-btn"><Plus className="w-4 h-4 mr-2" /> Add Lead</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B1324] border-white/10">
            <DialogHeader><DialogTitle className="font-heading text-xl">New Lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-sm">Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" data-testid="lead-name-input" /></div>
              <div><Label className="text-sm">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-sm">Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="bg-[#030712] border-white/10 h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#030712] border-white/10 mt-1" /></div>
              <Button onClick={create} className="btn-cta rounded-sm w-full" data-testid="save-lead-btn">Save Lead</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3 overflow-x-auto">
        {STAGES.map((s) => {
          const inStage = leads.filter((l) => l.stage === s.id);
          return (
            <div
              key={s.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, s.id)}
              className="surface rounded-md p-3 min-h-[60vh] relative overflow-hidden"
              data-testid={`kanban-col-${s.id}`}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background: `linear-gradient(90deg, ${s.color}, transparent 80%)` }}
              />
              <div className="flex items-center justify-between mb-3 pt-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }} />
                  <div className="label-eyebrow" style={{ color: s.color }}>{s.label}</div>
                </div>
                <span className="text-xs text-slate-500 font-mono">{inStage.length}</span>
              </div>
              <div className="space-y-2">
                {inStage.map((l) => (
                  <div
                    key={l.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, l)}
                    className="surface-elev rounded-sm p-3 cursor-grab active:cursor-grabbing hover:border-[#D4AF37]/50 transition-all hover:-translate-y-0.5"
                    data-testid={`lead-card-${l.id}`}
                    style={{ borderLeft: `2px solid ${s.color}` }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm flex-1 min-w-0">{l.customer_name}</div>
                      {l.source && l.source !== "manual" && (
                        <span
                          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0"
                          style={{ background: "rgba(212,175,55,0.15)", color: "var(--wakzy-gold)" }}
                          data-testid={`lead-source-${l.id}`}
                        >
                          {l.source}
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteLead(l); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        className="text-slate-500 hover:text-red-400 shrink-0"
                        title="Delete lead"
                        data-testid={`lead-delete-${l.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    {l.email && <div className="text-xs text-slate-500 mt-1 truncate">{l.email}</div>}
                    {l.phone && <div className="text-xs text-slate-500">{l.phone}</div>}
                    {l.notes && <div className="text-xs text-slate-400 mt-2 leading-snug line-clamp-3">{l.notes}</div>}
                    {l.customer_id && (
                      <Link
                        to={`/admin/customers/${l.customer_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-[#D4AF37] hover:underline inline-flex items-center gap-1 mt-2"
                        data-testid={`lead-open-customer-${l.id}`}
                      >
                        Open profile →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </AdminShell>
  );
}
